/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type ForwardRefExoticComponent, type RefAttributes } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import DropdownMenu, {
	DropdownItemCheckbox,
	DropdownItemCheckboxGroup,
} from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { type FilterOption } from '../../common/types';

import { messages } from './messages';

export type RovoFilterStyleButtonProps = {
	children: React.ReactNode;
	isSelected?: boolean;
} & React.ComponentProps<typeof Pressable>;

export const RovoFilterStyleButton: ForwardRefExoticComponent<
	Omit<RovoFilterStyleButtonProps, 'ref'> & RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, RovoFilterStyleButtonProps>(function RovoFilterStyleButton(
	{ children, isSelected, ...props },
	ref,
) {
	return (
		<Pressable
			ref={ref}
			xcss={cx(styles.filterButton, isSelected && styles.filterButtonSelected)}
			{...props}
		>
			{children}
		</Pressable>
	);
});

const styles = cssMap({
	filterButton: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		fontWeight: token('font.weight.medium'),
		fontFamily: token('font.family.body'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	filterButtonSelected: {
		borderColor: token('color.border.selected'),
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),

		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
	dropdownContainer: {
		position: 'relative',
		minWidth: '250px',
		maxHeight: '300px',
		overflowY: 'auto',
		borderRadius: token('radius.large'),
	},
	clearButtonContainer: {
		position: 'absolute',
		top: token('space.200'),
		right: token('space.200'),
	},
	clearButton: {
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.small'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		fontFamily: token('font.family.body'),
	},
	iconContainer: {
		width: '18px',
		height: '18px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	filterOption: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		color: token('color.text.subtle'),
	},
});

type RovoPromptFilterDropdownProps = {
	filterOptions: FilterOption[];
	triggerText: string;
	title: string;
	selectedFilters?: string[];
	onFilterChange?: (selectedFilters: string[]) => void;
	filterType: 'useCase' | 'role' | 'connector';
	renderIcon?: (option: FilterOption) => React.ReactNode;
};

const RovoPromptFilterDropdown = ({
	filterOptions,
	triggerText,
	title,
	selectedFilters = [],
	onFilterChange,
	filterType,
	renderIcon,
}: RovoPromptFilterDropdownProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const handleFilterToggle = (filterId: string) => {
		if (!onFilterChange) {
			return;
		}

		const newSelectedFilters = selectedFilters.includes(filterId)
			? selectedFilters.filter((id) => id !== filterId)
			: [...selectedFilters, filterId];

		// only tracks when a filter is selected, not removed
		if (!selectedFilters.includes(filterId)) {
			sendAnalyticsEvent({
				action: 'selected',
				actionSubject: filterType,
				actionSubjectId: 'promptLibraryFilter',
				attributes: {
					filterId: filterId,
				},
			});
		}

		onFilterChange(newSelectedFilters);
	};

	const handleClearAll = () => {
		if (!onFilterChange) {
			return;
		}
		onFilterChange([]);
	};

	return (
		<DropdownMenu
			spacing="compact"
			shouldRenderToParent
			trigger={({ triggerRef, isSelected, ...triggerProps }) => {
				const isFilterSelected = isSelected || selectedFilters.length > 0;
				return (
					<RovoFilterStyleButton ref={triggerRef} isSelected={isFilterSelected} {...triggerProps}>
						<Text
							as="span"
							weight="medium"
							color={isFilterSelected ? 'color.text.selected' : 'color.text.subtlest'}
						>
							{selectedFilters.length > 0
								? `${selectedFilters.length} ${
										selectedFilters.length === 1
											? triggerText.toLowerCase().replace(/s$/, '')
											: triggerText.toLowerCase()
									}`
								: triggerText}
						</Text>
						<div>
							<ChevronDownIcon label="" size="small" />
						</div>
					</RovoFilterStyleButton>
				);
			}}
		>
			<Box xcss={styles.dropdownContainer}>
				<Box xcss={styles.clearButtonContainer}>
					<Pressable xcss={styles.clearButton} onClick={handleClearAll} role="menuitem">
						{formatMessage(messages.clearAll)}
					</Pressable>
				</Box>
				<DropdownItemCheckboxGroup title={title} id="filter-options">
					{filterOptions.map((option) => {
						const showIcon = renderIcon?.(option);

						return (
							<DropdownItemCheckbox
								key={option.id}
								id={option.id}
								isSelected={selectedFilters.includes(option.id)}
								onClick={() => handleFilterToggle(option.id)}
							>
								<Box xcss={styles.filterOption}>
									{showIcon ? <Box xcss={styles.iconContainer}>{showIcon}</Box> : null}
									<span>{option.label}</span>
								</Box>
							</DropdownItemCheckbox>
						);
					})}
				</DropdownItemCheckboxGroup>
			</Box>
		</DropdownMenu>
	);
};

export default RovoPromptFilterDropdown;
